import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Activity, Server, Database, HardDrive, Clock, Eye, Users, UserCheck, UserX,
  Mail, CalendarDays, Newspaper, Image, ShieldAlert, ShieldCheck, Ban, RefreshCw, Loader2, TrendingUp, FileText,
  Bell, Send, Save, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const GOLD = "#D4AF37";
const BLUE = "#38BDF8";
const GREEN = "#10B981";
const RED = "#F87171";

const fmtUptime = (s) => {
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  if (d) return `${d}g ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
};

const kindLabel = { registered: "Registrati", newsletter: "Newsletter", anonymous: "Anonimi" };
const kindColor = { registered: GREEN, newsletter: GOLD, anonymous: BLUE };
const evtLabel = { failed_login: "Login fallito", lockout: "Blocco account", rate_limited: "Limite richieste" };
const evtColor = { failed_login: "text-[#FBBF24]", lockout: "text-[#F87171]", rate_limited: "text-[#38BDF8]" };
const WEEKDAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

function StatCard({ icon: Icon, label, value, sub, color = GOLD, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-white/10 bg-[#0F172A] p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="text-3xl font-display mt-2" style={{ color }}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function HealthPill({ label, status, icon: Icon }) {
  const ok = status === "online";
  return (
    <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2">
      <Icon className={`w-4 h-4 ${ok ? "text-[#10B981]" : "text-[#F87171]"}`} />
      <span className="text-sm">{label}</span>
      <span className={`ml-auto w-2 h-2 rounded-full ${ok ? "bg-[#10B981]" : "bg-[#F87171]"} ${ok ? "animate-pulse" : ""}`} />
    </div>
  );
}

export default function SiteKitDashboard() {
  const [health, setHealth] = useState(null);
  const [overview, setOverview] = useState(null);
  const [security, setSecurity] = useState(null);
  const [alerts, setAlerts] = useState({ enabled: true, recipient: "" });
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [testingAlert, setTestingAlert] = useState(false);
  const [summary, setSummary] = useState({ enabled: false, recipient: "", weekday: 0, hour: 8 });
  const [savingSummary, setSavingSummary] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, o, s, a, su] = await Promise.all([
        api.get("/analytics/health"),
        api.get(`/analytics/overview?days=${range}`),
        api.get("/security/events?limit=100"),
        api.get("/security/settings"),
        api.get("/analytics/summary-settings"),
      ]);
      setHealth(h.data); setOverview(o.data); setSecurity(s.data); setAlerts(a.data); setSummary(su.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { api.get("/analytics/health").then((r) => setHealth(r.data)).catch(() => {}); }, 20000);
    return () => clearInterval(t);
  }, []);

  const unblock = async (identifier) => {
    try { await api.post("/security/unblock", { identifier }); toast.success("Sblocco effettuato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveAlerts = async () => {
    setSavingAlerts(true);
    try {
      const { data } = await api.put("/security/settings", alerts);
      setAlerts(data);
      toast.success("Impostazioni avvisi salvate");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingAlerts(false); }
  };

  const testAlert = async () => {
    setTestingAlert(true);
    try {
      const { data } = await api.post("/security/test-alert");
      toast.success(`Avviso di prova inviato a ${data.recipient}. Controlla anche SPAM/Promozioni.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTestingAlert(false); }
  };

  const saveSummary = async () => {
    setSavingSummary(true);
    try {
      const { data } = await api.put("/analytics/summary-settings", summary);
      setSummary(data);
      toast.success("Impostazioni riepilogo salvate");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingSummary(false); }
  };

  const sendSummary = async () => {
    setSendingSummary(true);
    try {
      const { data } = await api.post("/analytics/weekly-summary/send");
      toast.success(`Riepilogo inviato a ${data.recipient}. Controlla anche SPAM/Promozioni.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSendingSummary(false); }
  };

  if (loading && !overview) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  const byKind = overview?.by_kind || {};
  const pieData = Object.keys(kindLabel).map((k) => ({ name: kindLabel[k], key: k, value: byKind[k]?.visitors || 0 }));
  const hasPie = pieData.some((d) => d.value > 0);
  const content = overview?.content || {};

  return (
    <div className="space-y-6" data-testid="sitekit-dashboard">
      {/* Header + refresh */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`sitekit-range-${d}`} onClick={() => setRange(d)}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${range === d ? "bg-gold text-[#020617] font-semibold" : "border border-white/10 text-muted-foreground hover:text-foreground"}`}>
              {d} giorni
            </button>
          ))}
        </div>
        <Button data-testid="sitekit-refresh" onClick={load} variant="outline" className="border-white/15 hover:border-gold/60">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </Button>
      </div>

      {/* Salute del sito */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2"><Activity className="w-5 h-5 text-gold" /> Salute del sito</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <HealthPill label="Backend" status={health?.backend} icon={Server} />
          <HealthPill label={`Database (${health?.db_latency_ms ?? "—"} ms)`} status={health?.database} icon={Database} />
          <HealthPill label="Archiviazione" status={health?.storage} icon={HardDrive} />
          <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2">
            <Clock className="w-4 h-4 text-gold" />
            <span className="text-sm">Attivo da</span>
            <span className="ml-auto text-sm font-semibold text-gold">{health ? fmtUptime(health.uptime_seconds) : "—"}</span>
          </div>
        </div>
      </div>

      {/* KPI visite */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testid="sitekit-total-views" icon={Eye} label="Visualizzazioni totali" value={overview?.total_views ?? 0} sub={`Ultimi ${range} giorni`} color={GOLD} />
        <StatCard testid="sitekit-unique" icon={Users} label="Visitatori unici" value={overview?.unique_visitors ?? 0} color={BLUE} />
        <StatCard testid="sitekit-registered" icon={UserCheck} label="Utenti registrati" value={byKind.registered?.visitors ?? 0} sub={`${byKind.registered?.views ?? 0} visite`} color={GREEN} />
        <StatCard testid="sitekit-anon" icon={UserX} label="Visitatori anonimi" value={byKind.anonymous?.visitors ?? 0} sub={`+ ${byKind.newsletter?.visitors ?? 0} da newsletter`} color={BLUE} />
      </div>

      {/* Grafici */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-white/10 bg-[#0F172A] p-5">
          <h4 className="text-sm font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-gold" /> Andamento visite</h4>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={overview?.daily || []} margin={{ left: -20, right: 8 }}>
              <defs>
                <linearGradient id="gReg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={GREEN} stopOpacity={0.6} /><stop offset="95%" stopColor={GREEN} stopOpacity={0} /></linearGradient>
                <linearGradient id="gNl" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={GOLD} stopOpacity={0.6} /><stop offset="95%" stopColor={GOLD} stopOpacity={0} /></linearGradient>
                <linearGradient id="gAn" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={BLUE} stopOpacity={0.6} /><stop offset="95%" stopColor={BLUE} stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0B1120", border: "1px solid #ffffff20", borderRadius: 8, color: "#fff" }} />
              <Legend formatter={(v) => kindLabel[v] || v} wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="registered" stackId="1" stroke={GREEN} fill="url(#gReg)" />
              <Area type="monotone" dataKey="newsletter" stackId="1" stroke={GOLD} fill="url(#gNl)" />
              <Area type="monotone" dataKey="anonymous" stackId="1" stroke={BLUE} fill="url(#gAn)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0F172A] p-5">
          <h4 className="text-sm font-semibold mb-2">Tipologia visitatori</h4>
          {hasPie ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {pieData.map((d) => <Cell key={d.key} fill={kindColor[d.key]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0B1120", border: "1px solid #ffffff20", borderRadius: 8, color: "#fff" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground text-center">Nessun dato di visita ancora raccolto.</div>
          )}
        </div>
      </div>

      {/* Top pagine + contenuti */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-white/10 bg-[#0F172A] p-5">
          <h4 className="text-sm font-semibold mb-4 flex items-center gap-2"><FileText className="w-4 h-4 text-gold" /> Pagine più viste</h4>
          {(overview?.top_pages || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun dato.</p>
          ) : (
            <div className="space-y-2" data-testid="sitekit-top-pages">
              {overview.top_pages.map((p) => {
                const max = overview.top_pages[0].views || 1;
                return (
                  <div key={p.path}>
                    <div className="flex items-center justify-between text-sm mb-1"><span className="truncate text-foreground/90">{p.path}</span><span className="text-gold font-semibold ml-2">{p.views}</span></div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gold rounded-full" style={{ width: `${(p.views / max) * 100}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0F172A] p-5">
          <h4 className="text-sm font-semibold mb-4">Contenuti del sito</h4>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: CalendarDays, label: "Eventi", v: content.events }, { icon: Users, label: "Atleti iscritti", v: content.athletes },
              { icon: Newspaper, label: "Notizie", v: content.news }, { icon: Mail, label: "Iscritti newsletter", v: content.subscribers },
              { icon: UserCheck, label: "Utenti account", v: content.users }, { icon: Image, label: "Media", v: content.media },
            ].map((c) => (
              <div key={c.label} className="flex items-center gap-3 rounded-lg bg-black/20 px-3 py-2.5 border border-white/5">
                <c.icon className="w-4 h-4 text-gold shrink-0" />
                <div><div className="text-lg font-semibold leading-none">{c.v ?? 0}</div><div className="text-[11px] text-muted-foreground mt-0.5">{c.label}</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Riepilogo settimanale */}
      <div className="rounded-xl border border-white/10 bg-[#0F172A] p-5" data-testid="summary-settings">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-gold" />
            <h4 className="text-sm font-semibold">Riepilogo settimanale via email</h4>
          </div>
          <div className="flex items-center gap-2">
            <Switch data-testid="summary-toggle" checked={!!summary.enabled} onCheckedChange={(v) => setSummary((s) => ({ ...s, enabled: v }))} />
            <span className="text-sm text-muted-foreground">{summary.enabled ? "Attivo" : "Disattivato"}</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Ricevi ogni settimana un'email con visite, nuovi iscritti, prossimi eventi e ultime notizie.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4 items-end">
          <div className="lg:col-span-1 sm:col-span-2">
            <label className="text-xs text-muted-foreground">Email destinatario</label>
            <Input data-testid="summary-recipient" type="email" value={summary.recipient || ""} onChange={(e) => setSummary((s) => ({ ...s, recipient: e.target.value }))} placeholder="admin@esempio.it" className="mt-1.5" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Giorno</label>
            <Select value={String(summary.weekday)} onValueChange={(v) => setSummary((s) => ({ ...s, weekday: parseInt(v) }))}>
              <SelectTrigger data-testid="summary-weekday" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Ora (UTC)</label>
            <Select value={String(summary.hour)} onValueChange={(v) => setSummary((s) => ({ ...s, hour: parseInt(v) }))}>
              <SelectTrigger data-testid="summary-hour" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button data-testid="summary-save" onClick={saveSummary} disabled={savingSummary} className="flex-1 bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
              {savingSummary ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Salva</>}
            </Button>
            <Button data-testid="summary-send-now" onClick={sendSummary} disabled={sendingSummary || !summary.recipient} variant="outline" className="border-white/15 hover:border-gold/60">
              {sendingSummary ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        {summary.last_sent && <p className="text-[11px] text-muted-foreground mt-3">Ultimo invio: {new Date(summary.last_sent).toLocaleString("it-IT")}</p>}
      </div>

      {/* Sicurezza */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-gold" /> Sicurezza</h3>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <StatCard testid="sec-failed" icon={ShieldAlert} label="Login falliti (24h)" value={security?.stats?.failed_login_24h ?? 0} color={RED} />
          <StatCard testid="sec-lockouts" icon={Ban} label="Account bloccati (24h)" value={security?.stats?.lockouts_24h ?? 0} color={GOLD} />
          <StatCard testid="sec-rate" icon={Activity} label="Limiti richieste (24h)" value={security?.stats?.rate_limited_24h ?? 0} color={BLUE} />
        </div>

        {/* Avvisi email in caso di attacco */}
        <div className="rounded-xl border border-white/10 bg-[#0F172A] p-5 mb-4" data-testid="sec-alert-settings">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-gold" />
              <h4 className="text-sm font-semibold">Avvisi email in caso di attacco</h4>
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="sec-alert-toggle" checked={!!alerts.enabled} onCheckedChange={(v) => setAlerts((a) => ({ ...a, enabled: v }))} />
              <span className="text-sm text-muted-foreground">{alerts.enabled ? "Attivi" : "Disattivati"}</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Ricevi un'email quando un account viene bloccato per troppi tentativi di accesso o quando un IP supera il limite di richieste (possibile attacco). Gli avvisi sono raggruppati per evitare troppe email.</p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 mt-4">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Email destinatario</label>
              <Input data-testid="sec-alert-recipient" type="email" value={alerts.recipient || ""} onChange={(e) => setAlerts((a) => ({ ...a, recipient: e.target.value }))} placeholder="admin@esempio.it" className="mt-1.5" />
            </div>
            <Button data-testid="sec-alert-save" onClick={saveAlerts} disabled={savingAlerts} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
              {savingAlerts ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Salva</>}
            </Button>
            <Button data-testid="sec-alert-test" onClick={testAlert} disabled={testingAlert || !alerts.recipient} variant="outline" className="border-white/15 hover:border-gold/60">
              {testingAlert ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Invia prova</>}
            </Button>
          </div>
        </div>

        {security?.blocked?.length > 0 && (
          <div className="rounded-xl border border-[#F87171]/30 bg-[#F87171]/5 p-4 mb-4" data-testid="sec-blocked-list">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2 text-[#F87171]"><Ban className="w-4 h-4" /> IP attualmente bloccati</h4>
            <div className="space-y-2">
              {security.blocked.map((b) => (
                <div key={b.identifier} data-testid={`sec-blocked-${b.ip}`} className="flex items-center justify-between text-sm bg-black/20 rounded-lg px-3 py-2">
                  <span className="font-mono">{b.ip} <span className="text-muted-foreground">· {b.email}</span></span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{b.count} tentativi</span>
                    <Button data-testid={`sec-unblock-${b.ip}`} size="sm" variant="outline" className="h-7 text-xs border-white/15 hover:border-gold/60" onClick={() => unblock(b.identifier)}>Sblocca</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-white/10 bg-[#0F172A] p-5">
          <h4 className="text-sm font-semibold mb-3">Registro eventi di sicurezza</h4>
          {(security?.events || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nessun evento di sicurezza registrato. 🛡️</p>
          ) : (
            <div className="divide-y divide-white/5 max-h-96 overflow-y-auto" data-testid="sec-events-list">
              {security.events.map((e) => (
                <div key={e.id} className="flex items-center gap-3 py-2.5 text-sm">
                  <Badge variant="outline" className={`border-white/15 ${evtColor[e.type] || ""}`}>{evtLabel[e.type] || e.type}</Badge>
                  <span className="font-mono text-xs text-muted-foreground">{e.ip}</span>
                  {e.email && <span className="text-muted-foreground truncate">{e.email}</span>}
                  <span className="ml-auto text-xs text-muted-foreground shrink-0">{new Date(e.created_at).toLocaleString("it-IT")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
